import {captureException} from '@sentry/react';
import {colors} from './toolsConstant';
import Color from 'color';
import type {z} from 'zod';
import {useEffect, useMemo} from 'react';
import {trpc, type TrpcUtils} from '@features/utils/trpc';
import type {Flow, Theme, themesUpdateSchema} from '@features/db/schema';
import {useAuthenticatedSession} from '@features/utils/useAuthenticatedSession';
import {generateId} from '@features/db/generateId';
import {optimisticallyUpdateFlow} from '@features/flow/flowHook';
import {useSession} from '@features/auth/authClient';

export type ThemeProps = Omit<
  Theme,
  'name' | 'fontFamilyBody' | 'userId' | 'createdAt' | 'id'
> & {'--font-family-body': string};

export const useFlowTheme = (
  flowId: string,
  screenId: string | undefined,
  flowFromContext?: Flow,
) => {
  const {data: flowFromQuery} = trpc.flow.getFlow.useQuery(
    {flowId},
    {enabled: !flowFromContext},
  );
  const flow = flowFromContext ?? flowFromQuery;

  const {data: activeStructure} = trpc.screens.getActiveStructure.useQuery(
    {screenId: screenId ?? ''},
    {enabled: screenId !== undefined},
  );

  const {data: screen} = trpc.screens.getScreen.useQuery(
    {screenId: screenId ?? ''},
    {enabled: screenId !== undefined, staleTime: 60_000, refetchOnMount: false},
  );

  // Priority: structure.themeId > screen.themeId > flow.themeId
  // screen.themeId is used during streaming (before structure exists)
  const themeId =
    screenId !== undefined
      ? (activeStructure?.themeId ?? screen?.themeId ?? flow?.themeId)
      : flow?.themeId;

  const {data: theme} = trpc.themes.getTheme.useQuery(
    {themeId: themeId ?? ''},
    {enabled: typeof themeId === 'string'},
  );

  return theme;
};

const updateCSSVariables = (value: string) => {
  document.documentElement.style.setProperty('--accent-bg', value);
  document.documentElement.style.setProperty(
    '--secondary-bg',
    value === colors[0]?.value
      ? '#ebebeb80'
      : Color(value).alpha(0.1).rgb().string(),
  );
  document.documentElement.style.setProperty('--secondary-bg-text', value);
};

/**
 * used for retro-compatibility with mobile components
 */
export const useUpdateCSSVariables = (flowId: string) => {
  const theme = useFlowTheme(flowId, undefined);

  useEffect(() => {
    if (theme === undefined) return;
    updateCSSVariables(theme['--accent']);
  }, [theme]);
};

const useUpdateTheme = (theme: Theme) => {
  const trpcUtils = trpc.useUtils();

  return trpc.themes.updateTheme.useMutation({
    onMutate: async ({updates}) => {
      const {revertOptimisticThemeUpdate} = await optimisticallyUpdateTheme(
        trpcUtils,
        theme.id,
        {
          action: 'update',
          update: updates,
        },
      );
      return revertOptimisticThemeUpdate;
    },
    onError: async (e, _, context) => {
      console.error('Error updating flow theme');
      captureException(e);
      await context?.();
    },
  });
};

const useDuplicateDefaultTheme = (
  flowId: string,
  theme: Theme,
  optimisticId: string,
) => {
  const session = useAuthenticatedSession();
  const trpcUtils = trpc.useUtils();

  return trpc.themes.duplicateDefaultTheme.useMutation({
    onMutate: async ({updates}) => {
      const newTheme = {
        ...theme,
        name: theme.name + ' (Edited)',
        ...updates,
        userId: session.user.id,
        id: optimisticId,
      } satisfies Theme;

      const [{revertOptimisticThemeUpdate}, updateFlowContext] =
        await Promise.all([
          optimisticallyUpdateTheme(trpcUtils, optimisticId, {
            action: 'updateOrInsert',
            update: newTheme,
          }),
          optimisticallyUpdateFlow(trpcUtils, flowId, {
            themeId: optimisticId,
          }),
        ]);

      return async () => {
        updateFlowContext?.revertOptimisticFlowUpdate();
        await revertOptimisticThemeUpdate();
      };
    },
    onError: async (e, _, context) => {
      console.error('Error updating flow theme');
      captureException(e);
      await context?.();
    },
  });
};

export const useUpdateFlowTheme = (flowId: string, theme: Theme) => {
  const {data} = useSession();
  const updateTheme = useUpdateTheme(theme);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optimisticId = useMemo(() => generateId(), [theme.id]);
  const duplicateDefaultTheme = useDuplicateDefaultTheme(
    flowId,
    theme,
    optimisticId,
  );

  return {
    mutate: (updates: z.infer<typeof themesUpdateSchema>) => {
      if (!data) {
        throw new Error('must be authenticated to update themes!');
      }

      if (theme.userId === data.user.id)
        updateTheme.mutate({themeId: theme.id, updates});
      else
        duplicateDefaultTheme.mutate({
          themeId: theme.id,
          updates,
          flowId,
          optimisticId,
        });
    },
  };
};
type ThemeUpdate =
  | {
      action: 'update';
      update: z.infer<typeof themesUpdateSchema>;
    }
  | {
      action: 'updateOrInsert';
      update: Theme;
    };

async function optimisticallyUpdateTheme(
  trpcUtils: TrpcUtils,
  id: string,
  update: ThemeUpdate,
) {
  await trpcUtils.themes.getVisibleThemes.cancel();
  await trpcUtils.themes.getTheme.cancel();
  const previousThemes = trpcUtils.themes.getVisibleThemes.getData();
  const previousTheme = trpcUtils.themes.getTheme.getData({themeId: id});

  trpcUtils.themes.getVisibleThemes.setData(undefined, (old) => {
    if (old === undefined) return undefined;
    const themeIndex = old.findIndex((theme) => theme.id === id);
    if (themeIndex !== -1) {
      return old.with(themeIndex, {...old[themeIndex], ...update.update});
    } else {
      if (update.action === 'updateOrInsert') {
        return [...old, update.update];
      }
    }
  });

  trpcUtils.themes.getTheme.setData({themeId: id}, (old) => {
    if (old) {
      return {...old, ...update.update};
    } else if (update.action === 'updateOrInsert') {
      return update.update;
    } else {
      return undefined;
    }
  });

  return {
    revertOptimisticThemeUpdate: async () => {
      trpcUtils.themes.getVisibleThemes.setData(undefined, previousThemes);
      trpcUtils.themes.getTheme.setData({themeId: id}, previousTheme);
      await trpcUtils.themes.getVisibleThemes.invalidate();
    },
  };
}

export function useVisibleThemes() {
  const trpcUtils = trpc.useUtils();
  const query = trpc.themes.getVisibleThemes.useQuery();
  const {data: themes} = query;

  useEffect(() => {
    if (themes)
      for (const theme of themes)
        trpcUtils.themes.getTheme.setData({themeId: theme.id}, theme);
  }, [themes, trpcUtils]);

  return query;
}
